import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Calendar, CheckCircle2, CornerDownRight, Flag, MessageSquare } from 'lucide-react'
import type { Label, Project, TaskCard, User } from '@witty-board/shared'
import { useTaskDetailQuery } from '../../api/tasks.js'
import { Avatar } from '../../components/Avatar.js'
import { Markdown } from '../../components/Markdown.js'
import { formatDateTime } from '../../lib/datetime.js'
import { dueUrgency, formatDueDate } from '../../lib/due-date.js'
import { useInView } from '../../lib/use-in-view.js'

/** Comentarios que se despliegan por tarea; el resto, abriendo el panel. */
const RECENT_COMMENTS = 3

const DUE_CLASSES = {
  overdue: 'text-danger',
  soon: 'text-warning',
  normal: 'text-text-muted',
} as const

interface ColumnTaskItemProps {
  task: TaskCard
  project: Project | undefined
  assignee: User | undefined
  usersById: Map<string, User>
  labelsById: Map<string, Label>
  /** En Mis tareas, igual que en su tarjeta: la procedencia es la información crítica. */
  showProjectKey: boolean
  onOpen: () => void
}

/**
 * Una tarea desplegada en la vista de columna (CLAUDE.md §6, fase 7): lo que
 * la tarjeta del tablero calla a propósito — prioridad, etiquetas,
 * descripción y últimos comentarios —, con la misma franja de color.
 */
export function ColumnTaskItem({
  task,
  project,
  assignee,
  usersById,
  labelsById,
  showProjectKey,
  onOpen,
}: ColumnTaskItemProps) {
  const { t, i18n } = useTranslation()
  const ref = useRef<HTMLLIElement>(null)
  const inView = useInView(ref)
  // Solo los comentarios necesitan el detalle, y se piden al acercarse al
  // viewport en vez de para toda la columna de golpe. Comparte caché con el
  // panel lateral: abrir la tarea después es instantáneo.
  const detail = useTaskDetailQuery(task.id, { enabled: inView && task.commentCount > 0 })

  const urgency = task.dueDate ? dueUrgency(task.dueDate) : null
  const labels = task.labelIds.flatMap((id) => {
    const label = labelsById.get(id)
    return label ? [label] : []
  })
  const recentComments = detail.data?.comments.slice(-RECENT_COMMENTS) ?? []

  return (
    <li
      ref={ref}
      style={{ borderLeftColor: project?.color }}
      className="flex flex-col gap-3 rounded-card border border-l-[3px] border-border bg-surface p-4"
    >
      <div className="flex items-start justify-between gap-3">
        <button
          type="button"
          onClick={onOpen}
          className="min-w-0 flex-1 rounded-card text-left text-card-title font-medium leading-snug text-text hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
          {task.title}
        </button>
        <span className="flex h-5 w-6 shrink-0 items-center justify-center rounded-card bg-surface-hi font-mono text-[11px] text-text-muted">
          {task.size ?? '–'}
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-meta text-text-muted">
        {showProjectKey && project && (
          <span className="font-medium">
            {project.key} · {project.name}
          </span>
        )}

        <span className="flex min-w-0 items-center gap-1">
          {assignee ? (
            <>
              <Avatar name={assignee.name} avatarUrl={assignee.avatarUrl} />
              <span className="truncate">{assignee.name}</span>
            </>
          ) : (
            t('common.unassigned')
          )}
        </span>

        <span className="flex items-center gap-1">
          <Flag className="h-3 w-3" aria-hidden="true" />
          <span className="sr-only">{t('task.fields.priority')}</span>
          {t(`task.priority.${task.priority}`)}
        </span>

        {task.dueDate && urgency && (
          <span className={`flex items-center gap-1 ${DUE_CLASSES[urgency]}`}>
            <Calendar className="h-3 w-3" aria-hidden="true" />
            <span className="sr-only">{t('task.fields.dueDate')}</span>
            {formatDueDate(task.dueDate, i18n.language)}
          </span>
        )}

        {task.completedAt && (
          <span className="flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3" aria-hidden="true" />
            {t('column.completedAt', { date: formatDateTime(task.completedAt, i18n.language) })}
          </span>
        )}

        {task.subtaskCount > 0 && (
          <span className="flex items-center gap-1">
            <CornerDownRight className="h-3 w-3" aria-hidden="true" />
            {task.subtaskDoneCount}/{task.subtaskCount}
          </span>
        )}

        {task.commentCount > 0 && (
          <span className="flex items-center gap-1">
            <MessageSquare className="h-3 w-3" aria-hidden="true" />
            {task.commentCount}
          </span>
        )}
      </div>

      {labels.length > 0 && (
        <ul className="flex flex-wrap gap-2" aria-label={t('task.fields.labels')}>
          {labels.map((label) => (
            <li
              key={label.id}
              className="flex h-6 items-center gap-2 rounded-card bg-surface-hi px-2 text-meta text-text"
            >
              <span
                className="h-2 w-2 shrink-0 rounded-full"
                style={{ backgroundColor: label.color }}
                aria-hidden="true"
              />
              {label.name}
            </li>
          ))}
        </ul>
      )}

      {task.description ? (
        <Markdown>{task.description}</Markdown>
      ) : (
        <p className="text-body text-text-muted">{t('column.noDescription')}</p>
      )}

      {task.commentCount > 0 && (
        <section className="flex flex-col gap-3 border-t border-border pt-3">
          <h3 className="text-meta font-medium text-text-muted">
            {task.commentCount > RECENT_COMMENTS
              ? t('column.recentCommentsOf', { shown: RECENT_COMMENTS, count: task.commentCount })
              : t('column.comments')}
          </h3>

          {detail.isError && <p className="text-meta text-danger">{t('task.detail.loadError')}</p>}

          {!detail.data && !detail.isError && (
            <div className="h-12 animate-pulse rounded-card bg-surface-hi" aria-hidden="true" />
          )}

          {recentComments.map((comment) => {
            const author = usersById.get(comment.authorId)
            return (
              <div key={comment.id} className="flex gap-2">
                {author && <Avatar name={author.name} avatarUrl={author.avatarUrl} size="md" />}
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                  <div className="flex items-center gap-2 text-meta text-text-muted">
                    <span className="font-medium text-text">{author?.name ?? '—'}</span>
                    <span>{formatDateTime(comment.createdAt, i18n.language)}</span>
                  </div>
                  <Markdown>{comment.body}</Markdown>
                </div>
              </div>
            )
          })}
        </section>
      )}
    </li>
  )
}
